if (!customElements.get('upsell-item')) {
  class UpsellItem extends HTMLElement {
    constructor() {
      super();
      this.data = null;
      this.locationData = [];
      this.select = null;
      this.button = null;
      this.initialized = false;
      this.pickupStore = null;
      this._onStoreChanged = this._onStoreChanged.bind(this);
      this._onVariantChanged = this._onVariantChanged.bind(this);
      this.retryCount = 0;
      this.requestVersion = 0;
      this._retryWhenReady = () => {
        if (this.inventoryStatus === 'error' && document.visibilityState !== 'hidden') this.retryAvailability();
      };
    }

    connectedCallback() {
      // The cart HTML can connect while its drawer is still closed. Initialize
      // now as well as after section replacements; opening is not a reconnect.
      this.initialize();
    }

    initialize() {
      if (this.initialized) return;
      this.initialized = true;
      this.hidden = false;
      this.removeAttribute('aria-hidden');
      this.setAttribute('aria-busy', 'true');
      requestAnimationFrame(() => {
        if (this.isConnected) this.init();
      });
    }

    disconnectedCallback() {
      clearTimeout(this.retryTimer);
      this.requestVersion++;
      this.loadingInventory = false;
      this.initialized = false;
      window.removeEventListener('online', this._retryWhenReady);
      document.removeEventListener('visibilitychange', this._retryWhenReady);
      document.removeEventListener('storechanged', this._onStoreChanged);
      this.select?.removeEventListener('change', this._onVariantChanged);
    }

    async init() {
      this.getElements();
      if (!this.select || !this.button) {
        this.hideUnavailableItem();
        return;
      }

      window.cartSliderFormSubmit?.('form.upsell', true);
      this.select.addEventListener('change', this._onVariantChanged);
      document.addEventListener('storechanged', this._onStoreChanged);

      window.addEventListener('online', this._retryWhenReady);
      document.addEventListener('visibilitychange', this._retryWhenReady);
      await this.loadAvailability();
    }

    retryAvailability() {
      if (this.loadingInventory) return;
      clearTimeout(this.retryTimer);
      this.retryCount = 0;
      this.loadAvailability();
    }

    async loadAvailability() {
      if (this.loadingInventory || !this.isConnected) return;
      clearTimeout(this.retryTimer);
      const version = ++this.requestVersion;
      this.loadingInventory = true;
      this.inventoryStatus = 'loading';
      this.locationData = [];
      this.updateSubmitButtonState();
      this.setAttribute('aria-busy', 'true');
      this.showInventoryStatus();
      try {
        const data = await this.fetchProductData();
        if (!this.isConnected || version !== this.requestVersion) return;
        this.data = data;
        this.inventoryStatus = 'ready';
        this.retryCount = 0;
        await this.refreshAvailability(true);
        this.showInventoryStatus();
      } catch (error) {
        if (!this.isConnected || version !== this.requestVersion) return;
        this.data = null;
        this.locationData = [];
        this.inventoryStatus = 'error';
        this.updateSubmitButtonState();
        this.showInventoryStatus();
        const delays = [750, 1750, 4000, 10000];
        if (this.retryCount < delays.length) {
          this.retryTimer = setTimeout(() => this.loadAvailability(), delays[this.retryCount++]);
        }
        console.warn('Upsell inventory unavailable; retry scheduled.', error);
      } finally {
        if (version === this.requestVersion && this.isConnected) {
          this.loadingInventory = false;
          this.setAttribute('aria-busy', 'false');
          this.classList.add('ready');
          this.refreshContainerVisibility();
        }
      }
    }

    showInventoryStatus() {
      if (!this.statusElement) {
        this.statusElement = document.createElement('div');
        this.statusElement.className = 'cart-upsell-item__inventory-status';
        this.statusElement.setAttribute('role', 'status');
        this.statusText = document.createElement('span');
        this.retryButton = document.createElement('button');
        this.retryButton.type = 'button';
        this.retryButton.className = 'cart-upsell-item__inventory-retry';
        this.retryButton.textContent = this.dataset.inventoryRetry || '↻';
        this.retryButton.addEventListener('click', () => this.retryAvailability());
        this.statusElement.append(this.statusText, this.retryButton);
        this.querySelector('.cart-upsell-item__body')?.append(this.statusElement);
      }
      this.statusElement.hidden = this.inventoryStatus === 'ready';
      this.statusText.textContent = this.inventoryStatus === 'loading'
        ? (this.dataset.inventoryLoading || '…') : (this.dataset.inventoryError || '');
      this.retryButton.hidden = this.inventoryStatus !== 'error';
    }

    getElements() {
      this.select = this.querySelector('select');
      this.button = this.querySelector('button[type="submit"]');
    }

    getPickupStore() {
      const handle = document.querySelector('#site-store-selector-handle [data-store-label][data-active-shop="true"]');
      const activeItem = document.querySelector('.store-selector-item--store--active');
      const name = handle?.getAttribute('data-store-label') || activeItem?.dataset?.title || '';
      const locationId = activeItem?.dataset?.locationId || '';

      if (!handle && !activeItem) return null;
      return { name: name.trim(), locationId: String(locationId) };
    }

    async _onStoreChanged() {
      if (!this.data) this.retryAvailability();
      else await this.refreshAvailability(true);
    }

    _onVariantChanged() {
      this.syncVariantPresentation();
      this.updateSubmitButtonState();
    }

    async refreshAvailability(preserveSelection = false) {
      const previousValue = preserveSelection ? this.select?.value : '';
      this.pickupStore = this.getPickupStore();
      this.computeAvailability();
      this.applyAvailabilityToUI();

      if (previousValue && this.isVariantSelectable(previousValue)) {
        this.select.value = previousValue;
      } else {
        this.autoselectFirstAvailable();
      }

      window.syncGiftThemeVariantSelector?.(this.select);

      this.syncVariantPresentation();
      this.updateSubmitButtonState();
      this.refreshContainerVisibility();
    }

    async fetchProductData() {
      const result = await window.ShopSanteInventory.load({ productId: this.dataset.productId });
      if (!result?.data?.product) throw new Error('Gift inventory unavailable');
      return result.data.product;
    }

    extractId(value) {
      const id = String(value || '');
      return id.includes('/') ? id.split('/').pop() : id;
    }

    matchesPickupStore(availability) {
      if (!availability || !this.pickupStore) return false;
      const availabilityId = this.extractId(availability.location?.id);
      const selectedId = this.extractId(this.pickupStore.locationId);

      if (selectedId && availabilityId) return selectedId === availabilityId;
      return availability.location?.name?.trim() === this.pickupStore.name;
    }

    availabilityAtPickup(nodes) {
      return (nodes || []).find(node => this.matchesPickupStore(node)) || null;
    }

    computeAvailability() {
      const variants = this.data?.variants?.nodes || [];
      const store = window.ShopSanteInventory.storeName(this.data);
      this.locationData = Array.from(this.select.options).map(option => {
        const variant = variants.find(item => this.extractId(item.id) === String(option.value));
        return { variantId: String(option.value), ...window.ShopSanteInventory.state(variant, store) };
      });
    }

    isVariantSelectable(variantId) {
      return Boolean(this.locationData.find(item => item.variantId === String(variantId))?.available);
    }

    applyAvailabilityToUI() {
      this.select.querySelectorAll('option').forEach(option => {
        const available = this.isVariantSelectable(option.value);
        const variantTitle = option.dataset.variantTitle || option.textContent.trim();
        const soldOutLabel = option.dataset.soldOutLabel || 'Sold out';
        option.disabled = !available;
        option.hidden = false;
        option.setAttribute('aria-disabled', String(!available));
        option.classList.toggle('cart-upsell-item__option--sold-out', !available);
        option.style.textDecoration = available ? '' : 'line-through';
        option.textContent = available ? variantTitle : `${variantTitle} — ${soldOutLabel}`;
      });

      if (typeof window.syncGiftOptionAvailability === 'function') {
        window.syncGiftOptionAvailability(this.select);
      }
      window.syncGiftThemeVariantSelector?.(this.select);

      const hasAvailableVariant = this.locationData.some(item => item.available);
      this.hidden = false;
      this.removeAttribute('aria-hidden');
      this.classList.toggle('is-unavailable', !hasAvailableVariant);
    }

    autoselectFirstAvailable() {
      const firstAvailable = Array.from(this.select.options).find(option => !option.disabled);
      if (firstAvailable) this.select.value = firstAvailable.value;
    }

    syncVariantPresentation() {
      const option = this.select?.selectedOptions?.[0];
      if (!option || option.disabled) return;

      const image = this.querySelector('[data-product-select] img');
      const imageUrl = option.dataset.upsellImage;

      if (image && imageUrl && imageUrl !== 'undefined') {
        image.src = imageUrl;
        image.srcset = '';
      }
    }

    updateSubmitButtonState() {
      if (!this.button || !this.select) return;

      const threshold = Number(this.getAttribute('data-upsell-threshold') || 0);
      const cartTotal = Number(this.getAttribute('data-upsell-cart-total') || 0);
      const variantAvailable = this.isVariantSelectable(this.select.value);
      const thresholdReached = cartTotal >= threshold;

      this.button.disabled = !(variantAvailable && thresholdReached);
      this.button.setAttribute('aria-disabled', String(this.button.disabled));
    }

    hideUnavailableItem() {
      this.hidden = true;
      this.setAttribute('aria-hidden', 'true');
      this.refreshContainerVisibility();
    }

    refreshContainerVisibility() {
      const container = this.closest('.cart-rewards-upsell');
      if (!container) return;

      const availableItems = Array.from(container.querySelectorAll('upsell-item')).filter(item => !item.hidden);
      const initializedItems = Array.from(container.querySelectorAll('upsell-item')).filter(item => item.classList.contains('ready'));

      if (initializedItems.length === container.querySelectorAll('upsell-item').length) {
        container.hidden = availableItems.length === 0;
      }
    }
  }

  customElements.define('upsell-item', UpsellItem);
}
